(ns app.front.dashboard.meetings
  (:require [app.domain.committee.model :as committee-model]
            [app.front.dashboard.common :as dashboard-common]
            [app.front.entities :as entities]
            [app.front.query.core :as query]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :refer [for-frag]]
            [app.front.view.committees.subs :as committee-subs]
            [app.front.view.pre-consultation-meetings.subs :as pre-con-subs]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [re-frame.core-instrumented :as rf]))


(defn- future-meeting? [meeting]
  (-> meeting
      :meeting/start
      date-util/before-now?
      not))


(rf/reg-sub ::future-meetings
  :<- [:session/authority-groups]
  :<- [::pre-con-subs/meetings]
  (fn [[ags meetings] _]
    (->> meetings
         (filter (fn [meeting]
                   (let [ag-ids-set    (->> ags (map :db/id) set)
                         meeting-ag-id (entities/get-id meeting :meeting/authority-group)]
                     (contains? ag-ids-set meeting-ag-id))))
         (filter future-meeting?))))


(rf/reg-sub ::next-three-meetings
  :<- [::future-meetings]
  :-> #(->> %
            (sort-by :meeting/start)
            (take 3)))


(defn- within-30-days? [meeting]
  (let [meeting-start (meeting :meeting/start)
        today         (js/Date.)
        plus-30       (date-util/add-days-to-date today 30)]
    (and (>= meeting-start today)
         (<= meeting-start plus-30))))


(rf/reg-sub ::next-three-meetings-within-30-days
  :<- [::next-three-meetings]
  :-> (partial filter within-30-days?))


(rf/reg-sub ::meetings-scheduled?
  :<- [::committee-subs/next-meetings]
  (fn [next-meetings _]
    (-> next-meetings seq boolean)))

(rf/reg-sub ::next-meetings-by-group
  :<- [::committee-subs/committees]
  (fn [committees _]
    (->> committees
         (group-by #(entities/get-id % :entity/parent))
         (keep (fn [[_ committees-in-group]]
                 (let [group    (-> committees-in-group first :entity/parent)
                       meetings (->> committees-in-group
                                     (keep committee-subs/next-meeting)
                                     (sort-by :committee-meeting/date))]
                   (when (and group (seq meetings))
                     {:group    group
                      :meetings meetings}))))
         (sort-by (comp :group/name :group)))))

(rf/reg-sub ::show-committees-dashlet?
  :<- [:session/clerk-user?]
  :<- [:session/meeting-coordinator-user?]
  :<- [::committee-subs/committees]
  (fn [[clerk? meeting-coordinator? committees] _]
    (and (or clerk? meeting-coordinator?)
         (not-empty committees))))

(rf/reg-sub ::any-pre-consultation-meetings?
  :<- [::next-three-meetings-within-30-days]
  :-> seq)

(rf/reg-sub ::show-not-found-pre-consultation-meetings?
  :<- [::any-pre-consultation-meetings?]
  :<- [:session/authority-for-permit-type? :permit-type/P]
  (fn [[any-meetings? authority-for-permit-type-P?] _]
    (and (not any-meetings?)
         authority-for-permit-type-P?)))

(rf/reg-sub ::show-pre-consultation-dashlet?
  :<- [::any-pre-consultation-meetings?]
  :<- [::show-not-found-pre-consultation-meetings?]
  (fn [[any-meetings? show-not-found?] _]
    (or any-meetings?
        show-not-found?)))

(rf/reg-sub ::show-meetings-dashlet
  :<- [::show-committees-dashlet?]
  :<- [::show-pre-consultation-dashlet?]
  (fn [[show-com-dashlet? show-pre-dashlet?] _]
    (or show-com-dashlet? show-pre-dashlet?)))

(defn- meeting-cards [meetings group-id]
  (let [lang (<sub [:language])]
    [:div.flex.flex-row.flex-wrap.mb-8.-mx-2
     (for-frag [{:keys [:db/id :committee-meeting/date item-count] :as committee} meetings]
       [dashboard-common/link-box-with-number
        {:title   [:span.bold ((<sub [::date-util/date-fn]) date)]
         :title-1 [:span.bold (committee-model/committee-name committee lang)]
         :href    (href/href :view/meetings {:group-id group-id} {:committee-id id})}
        item-count])]))

(defn- committee-meetings-legacy []
  (let [lang (<sub [:language])]
    [:div.flex.flex-row.flex-wrap.mb-8.-mx-2
     (for-frag [{:keys [:db/id
                        :committee-meeting/date
                        item-count]             :as committee} (<sub [::committee-subs/next-meetings])]
       [dashboard-common/link-box-with-number
        {:title   [:span.bold ((<sub [::date-util/date-fn]) date)]
         :title-1 [:span.bold (committee-model/committee-name committee lang)]
         :href    (href/href :view/committee {:committee-id id})}
        item-count])]))

(defn- dashlet [{:keys [tr header body button-href]}]
  [:<>
   [:h3.mb-5 header]
   body
   (cc/button {:href       button-href
               :kind       :tertiary
               :size       :small
               :aria-label (tr [:pre-consultation-meetings/view-all-meetings])}
              (tr [:view-all]))])

(defn- committee-group-dashlet [tr {:keys [group meetings]}]
  (let [group-id (:db/id group)]
    [:<>
     [dashlet
      {:tr          tr
       :header      (str (:group/name group) " " (tr [:committee/meetings]))
       :body        [meeting-cards meetings group-id]
       :button-href (href/href :view/meetings {:group-id group-id})}]
     [ui-sep/horizontal-line]]))

(defn- committee-meetings-by-group [tr]
  (let [groups-with-meetings (<sub [::next-meetings-by-group])]
    [:<>
     (for-frag [group-data groups-with-meetings]
       ^{:key (-> group-data :group :db/id)}
       [committee-group-dashlet tr group-data])]))

(defn- committee-body-legacy [tr]
  (cond
    (<sub [::query/fetching?
           :committee/next-meetings]) (cc/loading-spinner {:with-overlay false :small true})
    (<sub [::meetings-scheduled?]) [committee-meetings-legacy]
    :else [:div.text-gray-600.text-base.mb-8.mt-3 (tr [:committee/no-meetings])]))

(defn- committee-meetings-dashlet-legacy [tr]
  [dashlet
   {:tr          tr
    :header      (tr [:committee/committee])
    :body        [committee-body-legacy tr]
    :button-href (href/href :view/committee {:committee-id (-> (<sub [::committee-subs/committees]) first :db/id)})}])

(defn- committee-meetings-dashlet [tr]
  (cond
    (<sub [::query/fetching?
           :committee/next-meetings])
    (cc/loading-spinner {:with-overlay false :small true})

    (<sub [::ff/enabled? :feature-flag.id/domain-level-meetings])
    (if (<sub [::meetings-scheduled?])
      [committee-meetings-by-group tr]
      [:<>
       [:div.text-gray-600.text-base.mb-8.mt-3 (tr [:committee/no-meetings])]
       [ui-sep/horizontal-line]])

    :else
    [committee-meetings-dashlet-legacy tr]))

(defn- pre-consultation-meetings []
  [:div.flex.flex-row.flex-wrap.mb-8
   (for-frag [{:keys [:meeting/start
                      :meeting/topic]} (<sub [::next-three-meetings-within-30-days])]
     [dashboard-common/link-box-with-number
      {:title   [:span.bold ((<sub [::date-util/date-fn]) start)]
       :title-1 [:span.bold topic]
       :href    (href/href :view/pre-consultation-meetings
                           nil
                           {:selected-ms (inst-ms start)})}])])

(defn- pre-consultation-body [tr]
  (cond
    (<sub [::any-pre-consultation-meetings?])
    [pre-consultation-meetings]

    (<sub [::show-not-found-pre-consultation-meetings?])
    [:div.text-gray-600.text-base.mb-8.mt-3 (tr [:pre-consultation-meetings/no-meetings-within-days] [30])]))

(defn- pre-consultation-meetings-dashlet [tr]
  [dashlet
   {:tr          tr
    :header      (tr [:workspace.state/pre-consultation])
    :body        [pre-consultation-body tr]
    :button-href (href/href :view/pre-consultation-meetings)}])

(defn meetings-dashlet [tr]
  (let [show-com-dashlet?      (<sub [::show-committees-dashlet?])
        show-pre-dashlet?      (<sub [::show-pre-consultation-dashlet?])
        show-both-dashlets?    (and show-com-dashlet?
                                    show-pre-dashlet?)
        domain-level-meetings? (<sub [::ff/enabled? :feature-flag.id/domain-level-meetings])]
    [:section.dashlet
     [:h2.mb-8 (tr [:committee/meetings])]
     (when show-com-dashlet?
       [committee-meetings-dashlet tr])
     (when (and show-both-dashlets? (not domain-level-meetings?))
       [ui-sep/horizontal-line])
     (when show-pre-dashlet?
       [pre-consultation-meetings-dashlet tr])]))
